import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck } from "lucide-react";

const STATS = [
  { value: "5", label: "Acquisition Engines" },
  { value: "17", label: "Pipeline Stages" },
  { value: "100", label: "Point Opportunity Score" },
  { value: "9", label: "Specialized AI Agents" },
];

export default function Hero() {
  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 75% 30%, rgba(212,175,55,0.10), transparent 70%)",
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 mx-auto hidden max-w-7xl grid-cols-4 lg:grid"
      >
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="border-l border-white/5" />
        ))}
      </div>

      <div className="relative mx-auto grid max-w-7xl gap-16 px-6 pb-20 pt-36 lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:px-10 lg:pb-28 lg:pt-44">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="mb-6 inline-flex items-center gap-2 border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#D4AF37]">
            AI-Powered Partnership &amp; Distribution Engine
          </p>
          <h1
            data-testid="hero-headline"
            className="font-serif text-5xl font-semibold leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl"
          >
            Don&rsquo;t chase the customer.
            <span className="mt-3 block italic gold-text">
              Find the people who already have their attention.
            </span>
          </h1>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-zinc-400 sm:text-lg">
            KINGDOM PARTNERSHIP BLUEPRINT&trade; identifies the podcasts, creators, communities
            and stages your buyers already trust &mdash; then engineers the value-first path that
            earns you access to those audiences. Serve first. Build trust. Partner. Get on the
            stage. Measure the revenue.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#access"
              data-testid="hero-primary-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-7 py-3.5 text-sm font-bold uppercase tracking-[0.12em] text-black transition-transform duration-300 hover:scale-105 hover:brightness-110"
            >
              Request Early Access
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href="#method"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.12em] text-white transition-colors duration-300 hover:border-[#D4AF37]/60 hover:text-[#D4AF37]"
            >
              See the Methodology
            </a>
          </div>
          <p className="mt-6 flex items-center gap-2 text-xs text-zinc-500">
            <ShieldCheck className="h-4 w-4 text-[#047857]" />
            AI-assisted relationship building. Never spam automation.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="glow-gold relative overflow-hidden rounded-sm border border-white/10">
            <img
              src="https://images.pexels.com/photos/15913923/pexels-photo-15913923.jpeg?auto=compress&cs=tinysrgb&w=1200"
              alt="Glass chess king — strategy and authority"
              className="h-[420px] w-full object-cover lg:h-[540px]"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20" />
            <div className="float-slow absolute bottom-6 left-6 right-6 flex items-center justify-between border border-white/15 bg-black/60 px-5 py-4 backdrop-blur-xl">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-zinc-400">
                  Partnership Opportunity Score
                </p>
                <p data-testid="hero-score-chip" className="font-serif text-2xl font-semibold text-white">
                  92<span className="text-zinc-500">/100</span>
                </p>
              </div>
              <span className="rounded-full border border-[#D4AF37]/50 bg-[#D4AF37]/10 px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#D4AF37]">
                High Priority
              </span>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="relative border-t border-white/10">
        <div className="mx-auto grid max-w-7xl grid-cols-2 divide-x divide-white/10 lg:grid-cols-4">
          {STATS.map((s) => (
            <div key={s.label} className="px-6 py-8 lg:px-10">
              <p className="font-serif text-4xl font-semibold text-[#D4AF37]">{s.value}</p>
              <p className="mt-1 text-[11px] font-medium uppercase tracking-[0.2em] text-zinc-500">
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
